import { useEffect, useMemo, useRef } from "react"
import { Grid } from "@mui/material"
import { debounce, isEqual } from "lodash-es"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { DynamicConfigKey } from "../form/formTypes"
import type {
    DeliveryTimesField,
    MeetingServiceConfig,
    MainOrderFormikRef,
    MeetingServiceTabValues,
} from "../form/formTypes"
import { CreateOrderDialogMode, MeetingServicePriceListState, SelectedMainUserState } from "../../createOrderTypes"
import { DRAWER_WIDTH, DrawerContent } from "../drawer/DrawerContent"
import { ShopList } from "../shop/ShopList"

const useStyles = makeStyles()(() => ({
    contentSlide: {
        height: "100%",
        display: "grid",
        gridTemplateColumns: `1fr ${DRAWER_WIDTH}`,
    },
}))

/**
 * Keys owned by the resource booking timeline rather than by this form.
 *
 * This form seeds from a frozen snapshot of the outer values (see the `initialValues` memo in
 * MeetingServiceForm), so its copy goes stale as soon as the booking changes. They are synced back
 * only when this form actually changed them — the drawer meeting-time pickers do write `event`.
 */
const RESOURCE_BOOKING_OWNED_KEYS: ReadonlySet<string> = new Set<string>(["event", "fullResourcesList"])

type MeetingServiceFormContentProps = Readonly<{
    user: SelectedMainUserState
    handleClose: () => void
    handleOpenSelectPriceListsDialog: () => void
    meetingServiceConfiguration?: MeetingServiceConfig
    outerFormik: MainOrderFormikRef
    outerDeliveries?: DeliveryTimesField
    mode: CreateOrderDialogMode
    orderId?: number
}>

export function MeetingServiceFormContent({
    user,
    handleClose,
    handleOpenSelectPriceListsDialog,
    meetingServiceConfiguration,
    outerFormik,
    outerDeliveries,
    mode,
    orderId,
}: MeetingServiceFormContentProps) {
    const { classes } = useStyles()
    const { values, initialValues, setFieldValue } = useFormikContext<MeetingServiceTabValues>()
    const priceListState: MeetingServicePriceListState = values["priceList"]
    const { item: priceListItem } = priceListState

    // Read through a ref: the seed changes on every reinitialize, and putting it in the memo deps
    // would rebuild the debounce and drop whatever sync was already pending.
    const seedValuesRef = useRef(initialValues)
    seedValuesRef.current = initialValues

    const debouncedSyncValues = useMemo(
        () =>
            debounce((newValues: MeetingServiceTabValues) => {
                Object.entries(newValues).forEach(([key, value]) => {
                    const seedValue = (seedValuesRef.current as Record<string, unknown> | undefined)?.[key]
                    // Untouched booking keys are just this form echoing its own stale seed — skip them.
                    if (RESOURCE_BOOKING_OWNED_KEYS.has(key) && isEqual(seedValue, value)) return

                    const outerValue = (outerFormik?.current?.values as Record<string, unknown> | undefined)?.[key]
                    // Avoid writing the same value again (can cause reinitialize flicker and query re-fetch loops).
                    if (isEqual(outerValue, value)) return

                    outerFormik?.current?.setFieldValue(key, value)
                })
            }, 400),
        [outerFormik],
    )

    useEffect(() => {
        const outerPriceList = outerFormik.current?.values["priceList"]
        const outerId = outerPriceList?.item?.priceListId
        const localId = priceListItem?.priceListId

        if (outerId && outerId !== localId) {
            setFieldValue("priceList", outerPriceList)
        }
    }, [outerFormik.current?.values["priceList"], priceListItem?.priceListId])

    useEffect(() => {
        debouncedSyncValues(values)
    }, [values, debouncedSyncValues])

    // Flush the debounced sync immediately when multipleDeliveries changes
    // (e.g. stale extra-service removal) so the outer Formik / Order Summary cart stays in sync.
    const deliveries = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const prevDeliveriesRef = useRef(deliveries)
    // Read the current local value inside the "adopt outer changes" effect below without making it a
    // dependency — depending on it would re-run that effect on every local edit and push the outer
    // (not yet synced) value back on top of it.
    const deliveriesRef = useRef(deliveries)
    deliveriesRef.current = deliveries

    useEffect(() => {
        if (isEqual(prevDeliveriesRef.current, deliveries)) return
        prevDeliveriesRef.current = deliveries
        debouncedSyncValues.flush()
    }, [deliveries, debouncedSyncValues])

    // Adopt multipleDeliveries changes that originate outside this form: the Order Summary cart lives
    // in the dialog title (outer Formik), so its edits — deleting an order line / a product wish,
    // changing a quantity, setting a wish price — would otherwise never reach the values submitted here.
    // A change made inside this form does not alter the `outerDeliveries` reference, so this effect does
    // not re-run and cannot overwrite a fresh local value with a stale outer one; once the flush above
    // pushes it out, the incoming value is deep-equal and this bails out early.
    useEffect(() => {
        if (!outerDeliveries) return
        if (isEqual(outerDeliveries, deliveriesRef.current)) return

        prevDeliveriesRef.current = outerDeliveries
        setFieldValue(DynamicConfigKey.MULTIPLE_DELIVERIES, outerDeliveries)
    }, [outerDeliveries, setFieldValue])

    useEffect(() => {
        return () => {
            debouncedSyncValues.cancel()
        }
    }, [debouncedSyncValues])

    return (
        <Grid container className={classes.contentSlide}>
            {priceListItem && (
                <ShopList
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    handleCloseMainDialog={handleClose}
                    handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                    user={user}
                    orderId={orderId}
                    mode={mode}
                />
            )}
            {meetingServiceConfiguration && (
                <DrawerContent
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    user={user}
                    mode={mode}
                    merchantId={priceListItem?.merchantId}
                />
            )}
        </Grid>
    )
}
